import { NavArrows } from "@components/atoms/NavArrows"
import { BrowseCalendar, CalendarNavigator } from "@features/program/BrowseCalendar"
import { FilmPreviewTile } from "@components/film/FilmPreviewTile"
import { FilmTile } from "@components/film/FilmTile"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { ResponsivePopover } from "@components/ui/ResponsivePopover"
import { ResultBrowser } from "@components/util/ResultBrowser"
import { BROWSE_MODES, FilmSearchMode, FilmSearchResultItem, useFilmSearch } from "@features/program/useFilmSearch"
import { trackEvent } from "@features/tracking/tracking"
import { TrackingHelpers } from "@features/tracking/tracking-helpers"
import { BrowseFilmTileFragment, EventType } from "@generated-types"
import { BreakpointName, useBreakpoint } from "@hooks/useBreakpoint"
import dayjs from "dayjs"
import { useAppData } from "eddev/hooks"
import { useRoute, useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import {
  Fragment,
  HTMLProps,
  PropsWithChildren,
  ReactNode,
  startTransition,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { AllProgramFilters } from "./AllProgramFilters"
import { ProgramFilterPopup } from "./ProgramFilterPopup"
import { useProgramModes } from "./useProgramModes"
import { DateNavigator } from "@components/atoms/DateNavigator"
import { getEventTypesLabel } from "./program-filters"
import { AlphaBar } from "./AlphaBar"
import { useProgramBackButton } from "./useProgramBackButton"

const RESULTS_PER_ROW: { [K in BreakpointName]: number } = {
  BASE: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
  xxl: 5,
}

type Props = PropsWithChildren<{
  mode: FilmSearchMode
}>

export function BrowseProgramPage(props: Props) {
  const router = useRouter()
  const enableFiltering = useAppData((d) => d.sffSettings?.festivalSettings?.enableBrowseFiltering ?? false)

  const mode = props.mode
  const modes = useProgramModes()
  const modeInfo = BROWSE_MODES[mode]
  const browse = useFilmSearch({
    mode,
  })

  const backButton = useProgramBackButton()

  const route = useRoute()

  useEffect(() => {
    let label = "Back to Film & Events"
    if (browse.filterTags.length > 0) {
      label = "Back to Results"
    } else if (mode === "calendar") {
      label = "Back to Calendar"
    } else if (mode === "a-z") {
      label = "Back to Films A-Z"
    }
    backButton.remember(route.uri, label)
  }, [env.client ? route.uri : undefined, mode, browse.filterTags.length])

  useEffect(() => {
    router.preload("/program/")
    router.preload("/program/a-z/")
    router.preload("/program/by-date/")
    router.preload("/program/calendar/")

    trackEvent("Set Browse Mode", {
      BrowseMode: modeInfo.label,
      Toggled: false,
    })
  }, [])

  // Track breakpoints, which affect the number of items per row. The per-row value is needed for placing the preview box.
  const breakpoint = useBreakpoint()
  const perRow = RESULTS_PER_ROW[breakpoint]

  // Manage desktop film preview expander state
  const [_preview, setPreview] = useState<null | { item: FilmSearchResultItem; filterHash: number; closed?: boolean }>(
    null,
  )
  const preview = _preview?.filterHash === browse.filterHash ? _preview : null
  const previewIndex = preview ? browse.items.findIndex((item) => item.key === preview.item.key) : -1
  useEffect(() => {
    setPreview(null)
  }, [browse.filterHash])
  const previewing = preview && !preview.closed

  const lettersInUse = useMemo(() => {
    const letters = new Set<string>()

    for (const item of browse.items) {
      if (item.letter) {
        letters.add(item.letter)
      }
    }
    return letters
  }, [browse.items])

  const filterButtons = [
    <ResponsivePopover
      key="allFilters"
      popover={{
        side: "bottom",
        align: "start",
        padded: false,
      }}
      drawer={{
        handle: true,
        padded: false,
      }}
      trigger={<Button leftIcon="filters">Filters</Button>}
    >
      {({ close, as }) => <AllProgramFilters browse={browse} as={as} close={close} />}
    </ResponsivePopover>,
    <ProgramFilterPopup
      browse={browse}
      key="date"
      filterKey="date"
      trigger={<Button rightIcon="chevronDown">Date</Button>}
    />,
    <ProgramFilterPopup
      browse={browse}
      key="venue"
      filterKey="venues"
      trigger={<Button rightIcon="chevronDown">Venue</Button>}
    />,
    <ProgramFilterPopup
      browse={browse}
      key="genre"
      filterKey="genre"
      trigger={<Button rightIcon="chevronDown">Genre</Button>}
    />,
    <ProgramFilterPopup
      browse={browse}
      key="strand"
      filterKey="strand"
      trigger={<Button rightIcon="chevronDown">Strand</Button>}
    />,
    <ProgramFilterPopup
      browse={browse}
      key="type"
      filterKey="type"
      resetLabel="Reset"
      trigger={
        <Button rightIcon="chevronDown">
          Type
          {/* {getEventTypesLabel(browse.filters.get("type") as EventType[]) || "Films & Events"} */}
        </Button>
      }
    />,
  ]

  // Calendar date navigator
  const [calendarDate, setCalendarDate] = useState("")
  const calendarNav = useRef<CalendarNavigator>(null!)
  const calendarFilters = [
    <DateNavigator
      key="date"
      dates={browse.filterOptions.date?.dates ?? []}
      value={calendarDate}
      onValueChange={(value) => {
        calendarNav.current.goToDate(value)
      }}
    />,
  ]

  const tabs = useMemo(() => {
    return [...modes.entries()].map(([key, def]) => {
      return {
        value: key,
        label: def.label,
      }
    })
  }, [modes])

  useEffect(() => {
    if (browse.status === "success" && browse.items.length === 0) {
      const timer = setTimeout(() => {
        trackEvent("Browse No Results", {
          BrowseMode: modeInfo.label,
          TotalFilters: browse.filterTags.length,
        })
      }, 100)
      return () => {
        clearTimeout(timer)
      }
    }
  }, [browse.status, browse.items.length])

  useEffect(() => {
    if (route.hash) {
      const id = route.hash.substring(1)
      const el = document.getElementById(id)
      if (el) {
        requestAnimationFrame(() => {
          el.scrollIntoView({
            block: "start",
            behavior: "smooth",
          })
        })
      }
    }
  }, [route.hash])

  return (
    <div id="browse-page" data-mixpanel-template="Browse Page" data-mixpanel-browse-mode={BROWSE_MODES[mode]?.label}>
      <ResultBrowser
        loading={browse.status === "pending" && mode !== "featured"}
        noResults={browse.items.length === 0 && mode !== "featured"}
        filters={mode === "calendar" ? calendarFilters : enableFiltering ? filterButtons : null}
        tabs={
          tabs.length! > 1
            ? {
                items: tabs,
                value: mode,
                onValueChange: (mode) => {
                  const modeInfo = BROWSE_MODES[mode as FilmSearchMode]
                  const uri = modeInfo?.uri + (modeInfo?.includeFilters ? document.location.search : "")
                  router.navigate(uri)
                  trackEvent("Set Browse Mode", {
                    BrowseMode: modeInfo.label,
                    Toggled: true,
                  })
                },
              }
            : undefined
        }
        animationKey={"filters-" + browse.filterHash}
        itemsPerPage={24}
        totalItems={browse.items.length}
        fixedHeader
        header={
          <AnimatePresence>
            {browse.filterTags.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                layoutRoot
                className="w-grid mx-auto flex gap-2 flex-wrap"
              >
                <AnimatePresence mode="sync">
                  {browse.filterTags.map((tag) => (
                    <motion.button
                      className="inline-flex"
                      key={tag.key + "_" + tag.value}
                      layoutId={"filter-tag-" + tag.key + "_" + tag.value}
                      onClick={() => {
                        if (tag.all) {
                          browse.filters.set(tag.key as any, undefined)
                        } else if (tag.value) {
                          browse.removeFilter(tag.key, tag.value)
                        }
                      }}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                    >
                      <Chip rightIcon="cross" variant="primary">
                        {tag.type && <span className="mr-[0.5em] opacity-50">{tag.type}</span>}
                        {tag.label}
                      </Chip>
                    </motion.button>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        }
      >
        {({ page, sensor }) => {
          if (mode === "featured") return props.children

          if (mode === "calendar") {
            return (
              <BrowseCalendar
                items={browse.items}
                className="mb-9"
                onDateChange={setCalendarDate}
                navigatorRef={calendarNav}
              />
            )
          }

          if (mode === "a-z" || mode === "by-date") {
            const rows: ReactNode[] = []
            let currentRow: ReactNode[] = []
            let currentDate = ""
            let previewNextRow = false

            const addHeading = (text: string) => {
              rows.push(
                <div key={"heading" + text} className="col-span-full mt-5 mb-2">
                  <h2 className="type-title-m">{text}</h2>
                </div>,
              )
            }

            const finishRow = () => {
              if (currentRow.length > 0) {
                rows.push(...currentRow)
                currentRow = []
              }
              if (previewNextRow) {
                rows.push(
                  <PreviewBox
                    key="preview"
                    film={preview?.item.film!}
                    hasPrevious={previewIndex > 0}
                    hasNext={previewIndex < browse.items.length - 1}
                    closed={preview?.closed}
                    onGo={(delta) => {
                      const nextIndex = previewIndex + delta
                      const item = browse.items[nextIndex]
                      if (item) {
                        setPreview({ item, filterHash: browse.filterHash })
                      } else {
                        setPreview(null)
                      }
                    }}
                    onClose={() => {
                      setPreview({ ...preview!, closed: true })
                    }}
                  />,
                )
                previewNextRow = false
              }
            }

            if (mode === "a-z") {
              addHeading("Films A-Z")
            }

            browse.items.forEach((item, i) => {
              // if (i > page) return
              if (mode === "by-date") {
                let date = item.session?.info?.dateTime?.substring(0, 10) ?? ""
                if (date !== currentDate) {
                  currentDate = date
                  finishRow()
                  addHeading(dayjs(date).format("ddd D MMMM"))
                }
              }
              const isPreviewing = preview?.item.key === item.key
              if (isPreviewing) {
                previewNextRow = true
              }
              currentRow.push(
                <DelayedRow className="col-span-1 mt-4" key={item.key} data-film-letter={item.letter} delay={i}>
                  {i === page ? sensor : null}
                  <FilmTile
                    previewing={isPreviewing && !preview.closed}
                    onPreview={() => {
                      setPreview({ item, filterHash: browse.filterHash })
                    }}
                    film={item.film!}
                    session={item.session}
                    sessions={item.film.sessions!}
                    allowMobile
                    unloaded={i > page}
                  />
                </DelayedRow>,
              )
              if (currentRow.length === perRow) {
                finishRow()
              }
            })

            finishRow()

            return (
              <Fragment>
                <div className="w-grid mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-grid-gutter">
                  {rows}
                </div>
              </Fragment>
            )
          }
        }}
      </ResultBrowser>
      {mode === "a-z" && browse.items.length > 0 && !previewing && (
        <AlphaBar
          letters={lettersInUse}
          onSelect={(letter, dragging) => {
            const first = document.querySelector(`[data-film-letter="${letter}"]`) as HTMLDivElement
            if (first) {
              // const header = document.getElementById("site-header")
              // const filterBar = document.querySelector(".results-browser-filter-bar") as HTMLDivElement
              // const topHeight = (header?.offsetHeight ?? 0) + (filterBar?.offsetHeight ?? 0)
              // const firstTop = first.offsetTop
              // const scrollTarget = firstTop - topHeight
              if (document.scrollingElement) {
                // document.scrollingElement.scrollTop = scrollTarget
                requestAnimationFrame(() => {
                  first.scrollIntoView({
                    block: "start",
                    behavior: dragging ? "instant" : "smooth",
                  })
                  // document.scrollingElement!.scrollTo({
                  //   top: fi,
                  //   behavior: "instant",
                  // })
                })
              }
            }
          }}
        />
      )}
    </div>
  )
}

type PreviewBoxProps = PropsWithChildren<{
  film?: BrowseFilmTileFragment
  onClose?: () => void
  hasPrevious: boolean
  hasNext: boolean
  closed?: boolean
  onGo: (delta: number) => void
}>

function PreviewBox(props: PreviewBoxProps) {
  const ref = useRef<HTMLDivElement>(null!)
  const isFirst = useRef(true)

  if (props.closed) isFirst.current = true

  useLayoutEffect(() => {
    if (!ref.current) return
    let { top, height } = ref.current.getBoundingClientRect()
    // console.log({ top, height })

    const windowHeight = window.innerHeight
    const headerHeight = document.getElementById("site-header")?.clientHeight ?? 0
    const scrollTop = document.scrollingElement?.scrollTop!
    const scrollTarget = scrollTop + (top - (windowHeight - headerHeight) / 2 + height / 2) - headerHeight

    if (isFirst.current) {
      setTimeout(() => {
        document.scrollingElement?.scrollTo({
          top: scrollTarget,
          behavior: "smooth",
        })
      }, 100)
    } else {
      document.scrollingElement?.scrollTo({
        top: scrollTarget,
        behavior: "instant",
      })
    }
    isFirst.current = false
  }, [props.film])

  return (
    <div className="col-span-full relative theme-dark flex justify-center">
      <AnimatePresence>
        {!props.closed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-y-clip w-full mx-auto flex-none"
            data-mixpanel-area="Film Expander"
            {...TrackingHelpers.filmAttrs(props.film)}
          >
            <div ref={ref} className="pb-6 pt-1 will-change-transform w-grid mx-auto">
              <FilmPreviewTile key={props.film?.uri} film={props.film!} onClose={props.onClose} />
              <NavArrows
                canGoLeft={props.hasPrevious}
                canGoRight={props.hasNext}
                onLeft={() => props.onGo(-1)}
                onRight={() => props.onGo(1)}
                useKeyNav
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function DelayedRow({ delay, ...props }: HTMLProps<HTMLDivElement> & { delay: number }) {
  const [ready, setReady] = useState(delay < 30)

  useEffect(() => {
    if (ready) return
    const timer = setTimeout(
      () => {
        startTransition(() => {
          setReady(true)
        })
      },
      1000 + Math.floor(delay / 30) * 300,
    )

    return () => {
      clearTimeout(timer)
    }
  }, [delay])

  if (!ready) return null
  return <div {...props} />
}
